import { CheckCircle2, X, XCircle } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';

type Ton = 'succes' | 'erreur' | 'info';
type Notification = { id: number; ton: Ton; message: string };

type Contexte = {
  succes: (message: string) => void;
  erreur: (message: string) => void;
  info: (message: string) => void;
};

const ToastContext = createContext<Contexte | null>(null);

const DUREE = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const retirer = useCallback((id: number) => {
    setNotifications((n) => n.filter((x) => x.id !== id));
  }, []);

  const ajouter = useCallback(
    (ton: Ton, message: string) => {
      const id = Date.now() + Math.random();
      setNotifications((n) => [...n, { id, ton, message }]);
      setTimeout(() => retirer(id), DUREE);
    },
    [retirer],
  );

  const valeur = useMemo<Contexte>(
    () => ({
      succes: (m) => ajouter('succes', m),
      erreur: (m) => ajouter('erreur', m),
      info: (m) => ajouter('info', m),
    }),
    [ajouter],
  );

  return (
    <ToastContext.Provider value={valeur}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
      >
        {notifications.map((n) => (
          <div
            key={n.id}
            className={cn(
              'pointer-events-auto flex animate-slide-up items-start gap-2 rounded-md border px-3 py-2 text-sm shadow-pop',
              n.ton === 'succes' && 'border-success/30 bg-white text-ink',
              n.ton === 'erreur' && 'border-danger/40 bg-white text-ink',
              n.ton === 'info' && 'border-line bg-white text-ink',
            )}
          >
            {n.ton === 'succes' ? (
              <CheckCircle2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            ) : n.ton === 'erreur' ? (
              <XCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
            ) : null}
            <p className="flex-1">{n.message}</p>
            <button
              type="button"
              onClick={() => retirer(n.id)}
              className="shrink-0 rounded p-0.5 text-ink-muted hover:text-ink"
              aria-label="Fermer"
            >
              <X aria-hidden className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): Contexte {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast doit être utilisé dans un ToastProvider');
  return ctx;
}
